/**
 * The first sections of `cycle-step.html` for step 2 (f10.5, design D10 1-4):
 * the drop zone, "File dropped" and "What the system did with it". Every fact
 * is the batch's own, as the server recorded it.
 */

import { useRef, useState } from 'react';
import { AlertCircle, CalendarDays, CheckCircle2, FileSpreadsheet, Repeat, UploadCloud } from 'lucide-react';

import type { BillableDaysReview } from '@/api/client';
import { formatBytes, formatWhen, shortChecksum, statePresentation } from '@/routes/data-import/importView';
import { EmptyCell } from '@/ui/EmptyCell';

import { severityPill, transformations } from './billableDaysView';

function useFilePicker(onFile: (file: File) => void) {
  const input = useRef<HTMLInputElement>(null);
  const element = (
    <input
      ref={input}
      type="file"
      className="sr-only"
      aria-label="Billable-days report file"
      onChange={(event) => {
        const file = event.target.files?.item(0);
        if (file) onFile(file);
        event.target.value = '';
      }}
    />
  );
  return { open: () => input.current?.click(), element };
}

export function BillableDaysDropzone({ onFile, busy }: { onFile: (file: File) => void; busy: boolean }) {
  const [over, setOver] = useState(false);
  const picker = useFilePicker(onFile);
  return (
    <div
      className={`dropzone${over ? ' is-over' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        const file = event.dataTransfer.files.item(0);
        if (file) onFile(file);
      }}
    >
      <CalendarDays className="dz-icon" aria-hidden="true" />
      <div className="dz-title">Drop the billable-days report (country × month)</div>
      <div className="hint">
        Total rows are dropped and never recomputed; each country is matched to a country code before
        anything is stored.
      </div>
      <div className="btn-row dz-actions">
        <button type="button" className="btn btn-primary" disabled={busy} onClick={picker.open}>
          <UploadCloud aria-hidden="true" /> Choose file
        </button>
      </div>
      <div className="hint">The format is detected from the file&rsquo;s content, never from its name</div>
      {picker.element}
    </div>
  );
}

export function BillableDaysFileCard({
  review,
  onReplace,
  busy,
}: {
  review: BillableDaysReview;
  onReplace: (file: File) => void;
  busy: boolean;
}) {
  const { batch } = review;
  const picker = useFilePicker(onReplace);
  const state = statePresentation(batch.state.code);
  const staged = batch.state.code === 'STAGED' || batch.state.code === 'AWAITING_RESOLUTION';
  return (
    <div className="card">
      <div className="card-head">
        <h3>File dropped</h3>
        {batch.state.code === 'COMMITTED' ? (
          <span className="badge pill-success">
            {batch.is_current ? 'Committed — current billable days' : 'Committed'}
          </span>
        ) : staged ? (
          <span className="badge pill-info">Staged — not committed</span>
        ) : (
          <span className={`badge ${state.pill}`}>{state.label}</span>
        )}
      </div>
      <div className="card-body">
        <div className="dropzone is-filled">
          <div className="row us-eng-file">
            <FileSpreadsheet className="dz-icon" aria-hidden="true" />
            <div className="us-eng-file-text">
              <div className="dz-title truncate">{batch.original_filename ?? 'The uploaded file'}</div>
              <div className="hint">
                {formatBytes(batch.byte_size)} &middot; dropped {formatWhen(batch.uploaded_at)}
                {batch.uploaded_by ? ` by ${batch.uploaded_by.name}` : ''} &middot; batch #{batch.batch_number}
              </div>
            </div>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={picker.open}>
              <Repeat aria-hidden="true" /> Replace
            </button>
          </div>
          {picker.element}
        </div>
      </div>
    </div>
  );
}

export function BillableDaysProcessingCard({ review }: { review: BillableDaysReview }) {
  const { batch, issues } = review;
  return (
    <div className="card">
      <div className="card-head">
        <h3>What the system did with it</h3>
        <span className="hint">
          {issues.counts.ERROR ?? 0} error &middot; {issues.counts.WARNING ?? 0} warning &middot;{' '}
          {issues.counts.INFO ?? 0} info
        </span>
      </div>
      <div className="card-body stack">
        <dl className="dl cols-4">
          <div>
            <dt>Detected format</dt>
            <dd>
              {batch.detected_format} <span className="hint">sniffed by content</span>
            </dd>
          </div>
          <div>
            <dt>Header rows</dt>
            <dd>
              {batch.detected_header_row === null ? <EmptyCell /> : `1–${batch.detected_header_row}, folded`}
            </dd>
          </div>
          <div>
            <dt>Rows parsed / kept / dropped</dt>
            <dd>
              {batch.rows_in ?? '—'} / {batch.rows_out ?? '—'} / {batch.rows_dropped ?? '—'}
            </dd>
          </div>
          <div>
            <dt>Checksum</dt>
            <dd className="mono">{shortChecksum(batch.content_sha256)}</dd>
          </div>
        </dl>

        <div>
          <div className="stat-label">Transformations applied</div>
          <ul className="stack-sm us-eng-list">
            {transformations(review).map((item) => (
              <li key={item.text} className="row us-eng-transform">
                {item.ok ? (
                  <CheckCircle2 className="us-eng-ok" aria-hidden="true" />
                ) : (
                  <AlertCircle className="us-eng-warn" aria-hidden="true" />
                )}
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {issues.items.length > 0 && (
          <div>
            <div className="stat-label">Issues</div>
            <ul className="stack-sm us-eng-list">
              {issues.items.map((issue, index) => (
                <li key={`${issue.code}-${index}`} className="row us-eng-issue">
                  <span className={severityPill(issue.severity)}>{issue.severity}</span>
                  <span className="mono">{issue.code}</span>
                  <span className="us-eng-issue-text">
                    {issue.message}
                    {issue.source_row_number !== null && (
                      <span className="hint"> &middot; row {issue.source_row_number}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
